import { GameState } from '../core/GameState.js';
import skillTreesData from '../data/skill_trees.json';

/**
 * HTML/CSS panel for per-class skill tree allocation.
 * Opens during gameplay when player has unspent skill points.
 */
export class SkillTreePanel {
  constructor(parentEl) {
    this.parentEl = parentEl;
    this.el = null;
    this.isOpen = false;
    this.onAllocate = null; // callback when a skill is allocated
    this._create();
  }

  _create() {
    this.el = document.createElement('div');
    this.el.className = 'skill-tree-panel';
    this.el.innerHTML = `
      <div class="skill-tree-header">
        <h2>Skill Tree</h2>
        <div class="skill-points-display">Skill Points: <span id="skill-points-count">0</span></div>
      </div>
      <div class="skill-tree-branches" id="skill-tree-branches"></div>
      <button class="skill-tree-close" id="skill-tree-close">Close</button>
    `;
    this.el.style.display = 'none';
    this.parentEl.appendChild(this.el);

    const style = document.createElement('style');
    style.textContent = `
      .skill-tree-panel {
        position: absolute; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.92);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        z-index: 200; font-family: monospace; color: #ccc;
      }
      .skill-tree-header { text-align: center; margin-bottom: 10px; }
      .skill-tree-header h2 { color: #ffcc44; font-size: 14px; margin: 0 0 4px 0; }
      .skill-points-display { font-size: 11px; color: #88ff88; }
      .skill-tree-branches {
        display: flex; gap: 12px; max-width: 440px; width: 90%;
      }
      .skill-branch {
        flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15);
        padding: 8px; border-radius: 3px;
      }
      .skill-branch-name { font-size: 11px; font-weight: bold; text-align: center; margin-bottom: 8px; }
      .skill-node {
        background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1);
        padding: 4px 6px; margin-bottom: 4px; border-radius: 2px; font-size: 8px;
      }
      .skill-node.unlocked { border-color: rgba(100,255,100,0.4); background: rgba(0,80,0,0.3); }
      .skill-node.available { border-color: rgba(255,255,100,0.4); cursor: pointer; }
      .skill-node.available:hover { background: rgba(255,255,100,0.1); }
      .skill-node.locked { opacity: 0.4; }
      .skill-node-name { font-weight: bold; font-size: 9px; }
      .skill-node-desc { color: #888; font-size: 8px; }
      .skill-tree-close {
        margin-top: 10px; background: #aa4444; color: #fff; border: none;
        padding: 4px 16px; font-family: monospace; font-size: 11px; cursor: pointer;
        border-radius: 2px;
      }
      .skill-tree-close:hover { background: #cc5555; }
    `;
    this.parentEl.appendChild(style);

    document.getElementById('skill-tree-close').addEventListener('click', () => this.close());
  }

  open() {
    this.isOpen = true;
    this.el.style.display = 'flex';
    this._refresh();
  }

  close() {
    this.isOpen = false;
    this.el.style.display = 'none';
  }

  _refresh() {
    const classId = GameState.selectedClass || 'ironclad';
    const classTree = skillTreesData[classId];
    if (!classTree) return;

    const skillPoints = GameState.skillPoints || 0;
    const allocations = GameState.skillAllocations || {};
    document.getElementById('skill-points-count').textContent = skillPoints;

    const container = document.getElementById('skill-tree-branches');
    container.innerHTML = '';

    for (const [branchId, branch] of Object.entries(classTree.branches)) {
      const branchTier = allocations[branchId] || 0;
      const colorHex = '#' + parseInt(branch.color, 16).toString(16).padStart(6, '0');

      const branchEl = document.createElement('div');
      branchEl.className = 'skill-branch';

      let html = `<div class="skill-branch-name" style="color:${colorHex}">${branch.name}</div>`;

      for (let i = 0; i < branch.tiers.length; i++) {
        const tier = branch.tiers[i];
        const isUnlocked = i < branchTier;
        const isAvailable = i === branchTier && skillPoints > 0;
        const statusClass = isUnlocked ? 'unlocked' : isAvailable ? 'available' : 'locked';

        html += `<div class="skill-node ${statusClass}" data-branch="${branchId}" data-tier="${i}">
          <div class="skill-node-name">${tier.name}</div>
          <div class="skill-node-desc">${tier.description}</div>
        </div>`;
      }

      branchEl.innerHTML = html;
      container.appendChild(branchEl);
    }

    // Wire click handlers for available nodes
    container.querySelectorAll('.skill-node.available').forEach(node => {
      node.addEventListener('click', () => {
        const branchId = node.dataset.branch;
        this._allocateSkill(branchId);
      });
    });
  }

  _allocateSkill(branchId) {
    if (!GameState.skillPoints || GameState.skillPoints <= 0) return;

    if (!GameState.skillAllocations) GameState.skillAllocations = {};
    GameState.skillAllocations[branchId] = (GameState.skillAllocations[branchId] || 0) + 1;
    GameState.skillPoints--;

    this._refresh();

    if (this.onAllocate) {
      this.onAllocate(branchId);
    }
  }

  destroy() {
    if (this.el && this.el.parentElement) {
      this.el.parentElement.removeChild(this.el);
    }
  }
}
